/**
 * Runs a chunk-loading thunk, reattempting it once if the download failed fast.
 *
 * A second request recovers transient mobile-network failures without making
 * every page reload. If the retry proves the runtime's hashed URL is stale,
 * the Alpine loader owns the one-time page refresh needed to fetch a current
 * runtime.
 *
 * Webpack leaves both of its caches in a state a retry can use: the jsonp
 * runtime resets `installedChunks[chunkId]` to undefined before rejecting, and
 * loadScript deletes its `inProgress[url]` entry and removes the failed script
 * element. So a second `import()` reissues the request rather than replaying
 * the memoized rejection.
 *
 * A timed-out load is deliberately NOT retried. `output.chunkLoadTimeout`
 * governs it and is left at webpack's 120s default, and the timeout fires
 * through the same path as a network error — same `ChunkLoadError` name,
 * distinguished only by `type`. Retrying it would double the worst case to
 * 240s, and the caller gates Alpine's start on these promises, so that window
 * is one in which every component on the page is inert. A request that has
 * already hung for two minutes is not one a second attempt rescues.
 *
 * Only a ChunkLoadError is retried at all. Any other rejection comes from the
 * thunk's own body — a registration that threw once the module arrived — and
 * re-running it would repeat that work instead of recovering a download. The
 * second attempt's rejection propagates unchanged, so a chunk that is genuinely
 * gone still reaches the caller's failure handling.
 *
 * @param {function(): Promise} load Thunk that starts the import. Called again, unchanged, on retry.
 * @param {function(*): void} [onRetry] Receives the first attempt's rejection reason.
 * @returns {Promise} The first attempt's result, or the retry's.
 */
export function loadChunkWithRetry(load, onRetry) {
    // load() is called inside an executor so a thunk that throws synchronously
    // yields a rejected promise rather than unwinding into the caller, which
    // runs at module-evaluation time where an escaping throw would stop Alpine
    // starting at all. It is called here rather than passed to .then(), which
    // ignores a non-callable handler and would turn a mis-wired call site into
    // a promise that fulfils with undefined — the failure handling downstream
    // never runs, so the components' roots are left neither registered nor
    // ignored. The retry below is already inside a catch callback, where a
    // synchronous throw becomes a rejection on its own.
    return new Promise((resolve) => resolve(load())).catch((error) => {
        if (!isRetriableChunkError(error)) {
            throw error;
        }

        if (onRetry) {
            onRetry(error);
        }

        return load();
    });
}

/**
 * Webpack stamps `name = 'ChunkLoadError'` and `type = <errorType>` on the error
 * its jsonp runtime builds, where errorType is 'timeout' for the load-timeout
 * path, 'missing' for a script that loaded without registering its chunk, and
 * the DOM event type ('error') for a failed request.
 *
 * The name is matched on its own because webpack's message does not contain it:
 * the message is 'Loading chunk <id> failed.\n(<type>: <url>)'. The
 * 'ChunkLoadError: ...' spelling that appears in Sentry is Sentry rendering
 * `name: message`, not a string the runtime ever produces.
 */
export function isRetriableChunkError(error) {
    return isChunkLoadError(error) && error.type !== 'timeout';
}

export function isChunkLoadError(error) {
    return Boolean(error) && error.name === 'ChunkLoadError';
}
